import { useEffect } from 'react'
import {
  useGetAllOrderLines,
  useGetOrderPicklist,
  useGetUnpickedItems,
  useGetCompletedDeliveries,
  useGetTotes,
} from './backend/scanPage'
import { useGetProducts } from './backend/inventoryPage'
import { useGetCustomerSummaries } from './backend/customersPage'

// Stale-while-revalidate wrappers for read-only backend queries.
//
// The generated hooks reset their internal `data` to null on every re-mount,
// so navigating away and back to a page shows an empty/loading state until the
// query round-trips again. These wrappers keep the last successful result in a
// module-level cache and seed `data` from it immediately on re-mount, while the
// page's own trigger() call revalidates in the background.
//
// Only read-only (SELECT/GET) functions belong here — never wrap a mutation.

type QueryHook = {
  data: unknown
  loading: boolean
  error: string | null
  trigger: (
    params?: Record<string, unknown>,
    options?: { skipCache?: boolean },
  ) => unknown
}

function useCached<T extends QueryHook>(hook: T, store: { value: unknown }): T {
  useEffect(() => {
    if (hook.data !== null && hook.data !== undefined) {
      store.value = hook.data
    }
  }, [hook.data])
  return { ...hook, data: hook.data ?? store.value ?? null }
}

// ── scanPage / ordersPage ──
const orderLinesStore = { value: undefined as unknown }
export function useGetAllOrderLinesCached() {
  return useCached(useGetAllOrderLines(), orderLinesStore)
}

const orderPicklistStore = { value: undefined as unknown }
export function useGetOrderPicklistCached() {
  return useCached(useGetOrderPicklist(), orderPicklistStore)
}

const unpickedItemsStore = { value: undefined as unknown }
export function useGetUnpickedItemsCached() {
  return useCached(useGetUnpickedItems(), unpickedItemsStore)
}

const completedDeliveriesStore = { value: undefined as unknown }
export function useGetCompletedDeliveriesCached() {
  return useCached(useGetCompletedDeliveries(), completedDeliveriesStore)
}

const totesStore = { value: undefined as unknown }
export function useGetTotesCached() {
  return useCached(useGetTotes(), totesStore)
}

// ── inventoryPage ──
const productsStore = { value: undefined as unknown }
export function useGetProductsCached() {
  return useCached(useGetProducts(), productsStore)
}

// ── customersPage ──
const customerSummariesStore = { value: undefined as unknown }
export function useGetCustomerSummariesCached() {
  return useCached(useGetCustomerSummaries(), customerSummariesStore)
}
